'use client';

import { withoutPiiInUrl } from '@mv/tracking-core';
import { useEffect } from 'react';
import { useI18n } from '@/modules/i18n/useI18n';
import { readTrackingUser } from '@/modules/tracking/analytics/trackingUser';
import useTrackEvent from '@/modules/tracking/analytics/useTrackEvent';
import canUseDOM from '@/modules/utils/canUseDOM';
import { getClientSideURL } from '@/modules/utils/getURL';

interface Props {
	active: {
		path?: string | null;
		title?: string | null;
	};
	canonical: {
		path?: string | null;
		title?: string | null;
	};
	flow?: {
		name?: string | null;
		step?: string | null;
	};
}

export default function TrackPageView({ active, canonical, flow }: Props) {
	const { locale } = useI18n();
	const trackEvent = useTrackEvent();

	useEffect(() => {
		const user = readTrackingUser();

		// Extract click IDs from URL parameters for attribution tracking
		const urlParams = new URLSearchParams(window.location.search);
		const fbclid = validateClickId(urlParams.get('fbclid'));
		const gclid = validateClickId(urlParams.get('gclid'));

		const clickIds: { fbclid?: string; gclid?: string } = {};
		if (fbclid) clickIds.fbclid = fbclid;
		if (gclid) clickIds.gclid = gclid;

		trackEvent('page_view', {
			canonical: canonical.path
				? {
						page_location: sanitizeUrl(
							new URL(canonical.path + window.location.search + window.location.hash, getClientSideURL()),
						),
						page_path: canonical.path,
						page_title: canonical.title,
						language: 'en',
					}
				: null,
			sanitized: active.path
				? {
						page_location: sanitizeUrl(
							new URL(active.path + window.location.search + window.location.hash, getClientSideURL()),
						),
						page_path: active.path,
						page_title: active.title,
						language: locale,
					}
				: null,
			flow:
				flow?.name || flow?.step
					? {
							name: flow?.name ?? null,
							step: flow?.step ?? null,
						}
					: null,
			user,
			a11y: {
				reduced_motion: mediaQueryValue('prefers-reduced-motion'),
				color_scheme: mediaQueryValue('prefers-color-scheme', ['dark', 'light']),
				forced_colors: mediaQueryValue('forced-colors'),
				prefers_contrast: mediaQueryValue('prefers-contrast', ['more', 'less', 'custom']),
			},
			...(Object.keys(clickIds).length > 0 && { clickIds }),
		});
	}, [trackEvent, active.path, active.title, canonical.path, canonical.title, flow?.name, flow?.step, locale]);

	return null;
}

/**
 * Check the value for a media query.
 * Returns a boolean if a match is found, the value if it is within the list of values,
 * or undefined no values match or if the browser doesn't support media queries.
 */
function mediaQueryValue(q: string, values?: string[]) {
	if (!canUseDOM || !window.matchMedia) {
		return undefined;
	}

	if (!values) {
		return window.matchMedia(`(${q})`).matches;
	}

	return values.find((value) => window.matchMedia(`(${q}: ${value})`).matches);
}

function sanitizeUrl(url: string | URL) {
	try {
		return withoutPiiInUrl(new URL(url)).toString();
	} catch (e) {
		console.error('Invalid URL:', url, e);
		return url.toString();
	}
}

// Utility to validate click ID - allow only alphanumeric, underscore, hyphen, dot, max 500 chars
function validateClickId(val: string | null): string | undefined {
	if (val && val.length <= 500 && /^[a-zA-Z0-9_.-]+$/.test(val)) {
		return val;
	}
	return undefined;
}
